<template>
  <AssetCard :view-mode="viewMode"
    :empty-title="digitalEntity ? null : 'Aucune entité numérique ' + (isInput ? 'd\'entrée' : 'de sortie')"
    :empty-message="null" :created-at="digitalEntity?.created_at ?? null" :memos-count="memosCount"
    :show-edit-button="showEditButton && !!digitalEntity && viewMode !== 'table'" :show-delete-button="showDeleteButton && !!digitalEntity && viewMode !== 'table'"
    :table-grid-class="'flex items-center'" :class="customClass" :is-owned="isOwned" :is-public="isPublic"
    :is-private="!isPublic" :show-ownership-badge="showOwnershipBadge" :user-id="userId"
    :can-edit="canEditDigitalEntity" :can-delete="canDeleteDigitalEntity" @click="handleClick"
    @edit="digitalEntity && $emit('edit', digitalEntity)" 
    v-on="viewMode !== 'table' ? { delete: handleDelete } : {}">
    <!-- Mode Table -->
    <template #table-row="{ classes }">
      <!-- Nom et URL -->
      <div class="flex items-center gap-2 min-w-0 flex-[1.25]">
        <component :is="isInput ? FileInput : FileOutput" :class="classes.icon" />
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <span :class="[classes.title, 'truncate']">{{ digitalEntity?.appellation || 'Sans nom' }}</span>
            <a v-if="digitalEntity?.url" :href="digitalEntity.url" target="_blank" :class="classes.link">
              <LinkIcon :class="classes.linkIcon" />
            </a>
          </div>
          <div class="flex items-center gap-2 text-xs text-gray-500">
            <span v-if="digitalEntity?.type">{{ digitalEntity.type }}</span>
            <span v-if="digitalEntity?.type && digitalEntity?.format" class="text-gray-300">•</span>
            <span v-if="digitalEntity?.format">{{ digitalEntity.format }}</span>
            <span v-if="digitalEntity?.format && digitalEntity?.version" class="text-gray-300">•</span>
            <span v-if="digitalEntity?.version">v{{ digitalEntity.version }}</span>
          </div>
        </div>
      </div>

      <!-- Propriétaire -->
      <div class="hidden md:flex items-center text-sm text-gray-600 min-w-0 flex-1 px-3">
        <span class="truncate">{{ digitalEntity?.owner || 'Non spécifié' }}</span>
      </div>

      <!-- Copyright -->
      <div class="hidden lg:flex items-center text-sm text-gray-600 min-w-0 flex-1 px-3">
        <span class="truncate">{{ digitalEntity?.copyright || 'Non spécifié' }}</span>
      </div>

      <!-- Lien -->
      <div v-if="digitalEntity?.de_link" class="flex items-center gap-1 text-sm text-gray-600 min-w-0 px-3">
        <LinkIcon :class="classes.footerIcon" />
        <span>Lien</span>
      </div>

      <!-- Actions -->
      <TableRowActions :classes="classes" :show-edit-button="showEditButton" :show-delete-button="showDeleteButton"
        :can-edit="canEditDigitalEntity" :can-delete="canDeleteDigitalEntity" @edit="handleEdit"
        @delete="handleDelete" />
    </template>

    <!-- Mode Memos Table -->
    <template #memos-table-icon>
      <component :is="isInput ? FileInput : FileOutput" class="h-5 w-5 text-gray-500 shrink-0" />
    </template>

    <template #memos-table-content="{ classes }">
      <template v-if="digitalEntity">
        <div class="flex items-center gap-2">
          <h3 :class="classes.title">{{ digitalEntity.appellation || 'Sans nom' }}</h3>
          <a v-if="digitalEntity.url" :href="digitalEntity.url" target="_blank" :class="classes.link">
            <LinkIcon :class="classes.linkIcon" />
          </a>
        </div>
        <div class="flex items-center gap-2 text-xs text-gray-500">
          <span v-if="digitalEntity.type">{{ digitalEntity.type }}</span>
          <span v-if="digitalEntity.type && digitalEntity.format" class="text-gray-300 shrink-0">•</span>
          <span v-if="digitalEntity.format">{{ digitalEntity.format }}</span>
          <span v-if="digitalEntity.format && digitalEntity.version" class="text-gray-300 shrink-0">•</span>
          <span v-if="digitalEntity.version">v{{ digitalEntity.version }}</span>
        </div>
        <div v-if="digitalEntity.description" class="text-xs text-gray-500 mt-1 truncate">
          {{ digitalEntity.description }}
        </div>
      </template>
      <div v-else class="text-gray-500 text-sm flex items-center gap-2">
        <span>Aucune entité numérique {{ isInput ? "d'entrée" : 'de sortie' }}</span>
      </div>
    </template>

    <!-- Mode Default -->
    <template #default="{ classes }">
      <div class="flex items-center gap-4 mb-2">
        <h2 :class="classes.title">{{ digitalEntity?.appellation || 'Sans nom' }}</h2>
        <div v-if="digitalEntity?.url" class="flex items-center">
          <a :href="digitalEntity.url" target="_blank" :class="classes.link">
            <LinkIcon :class="classes.linkIcon" />
          </a>
        </div>
      </div>

      <p v-if="digitalEntity?.description" class="text-sm text-gray-600 mb-4">
        {{ digitalEntity.description }}
      </p>

      <div v-if="digitalEntity?.type || digitalEntity?.format || digitalEntity?.version"
        class="flex flex-wrap gap-2 mb-4">
        <span v-if="digitalEntity.type" class="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">
          {{ digitalEntity.type }}
        </span>
        <span v-if="digitalEntity.format" class="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">
          {{ digitalEntity.format }}
        </span>
        <span v-if="digitalEntity.version" class="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">
          v{{ digitalEntity.version }}
        </span>
      </div>
    </template>

    <!-- Mode Default Info -->
    <template #info="{ classes }">
      <div v-if="digitalEntity?.de_link" :class="classes.iconWithText">
        <LinkIcon :class="classes.icon" />
        <span>Lien disponible</span>
      </div>

      <div v-if="digitalEntity?.file_spec" :class="classes.iconWithText">
        <FileTextIcon :class="classes.icon" />
        <span>{{ digitalEntity.file_spec }}</span>
      </div>

      <div v-if="digitalEntity?.owner" :class="classes.iconWithText">
        <UserIcon :class="classes.icon" />
        <span>{{ digitalEntity.owner }}</span>
      </div>

      <div v-if="digitalEntity?.copyright" :class="classes.iconWithText">
        <CopyrightIcon :class="classes.icon" />
        <span>{{ digitalEntity.copyright }}</span>
      </div>
    </template>


  </AssetCard>
</template>

<script lang="ts" setup>

import {
    Copyright as CopyrightIcon,
    FileInput,
    FileOutput,
    FileText as FileTextIcon,
    Link as LinkIcon,
    User as UserIcon
} from 'lucide-vue-next';
import type { PropType } from 'vue';
import { computed, ref } from 'vue';
import type { DigitalEntity } from '../../api/types/entities/digitalEntity/types';
import { useAuthStore } from '../../stores/auth';
import AssetCard from '../UI/AssetCard.vue';
import TableRowActions from '../UI/TableRowActions.vue';

const props = defineProps({
  digitalEntity: {
    type: Object as PropType<DigitalEntity | null>,
    default: null
  },
  isInput: {
    type: Boolean,
    default: true
  },
  viewMode: {
    type: String as PropType<'default' | 'table' | 'memostable'>,
    default: 'default'
  },
  showActions: {
    type: Boolean,
    default: true
  },
  showEditButton: {
    type: Boolean,
    default: false
  },
  showDeleteButton: {
    type: Boolean,
    default: false
  },
  customClass: {
    type: String,
    default: ''
  },
  memosCount: {
    type: Number,
    default: 0
  },
  // Nouvelles props pour les permissions et propriété
  isOwned: {
    type: Boolean,
    default: false
  },
  isPublic: {
    type: Boolean,
    default: false
  },
  showOwnershipBadge: {
    type: Boolean,
    default: true
  },
  userId: {
    type: String,
    default: undefined
  }
});

const emit = defineEmits(['edit', 'delete', 'click']);

const authStore = useAuthStore();

// Computed pour les permissions
const canEditDigitalEntity = computed((): boolean => {
  return props.digitalEntity ? authStore.canEditAsset(props.digitalEntity as unknown as { user_id: string; is_public: boolean }) : false;
});

const canDeleteDigitalEntity = computed((): boolean => {
  return props.digitalEntity ? authStore.canDeleteAsset(props.digitalEntity as unknown as { user_id: string; is_public: boolean }) : false;
});

const handleClick = (): void => {
  if (props.digitalEntity) {
    emit('click', props.digitalEntity);
  }
};

const handleEdit = (): void => {
  if (props.digitalEntity) {
    emit('edit', props.digitalEntity);
  }
};

// Protection contre les doubles clics
const isDeleting = ref(false);

const handleDelete = (): void => {
  console.log('[DigitalEntityCard] handleDelete appelé, isDeleting:', isDeleting.value);
  if (isDeleting.value) return; // Éviter les doubles clics
  
  isDeleting.value = true;
  console.log('[DigitalEntityCard] Émission de l\'événement delete pour:', props.digitalEntity?.uuid);
  emit('delete', props.digitalEntity);
  
  // Réinitialiser après un délai
  setTimeout(() => {
    isDeleting.value = false;
  }, 1000);
};
</script>

<style scoped></style>